import { FC, useEffect, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/store'
import { useLazyQuery, useMutation } from '@apollo/client'
import { Redirect, useHistory, useParams } from 'react-router-dom'

import { BACKEND_URL, graphqlEndpoint } from 'AppSettings'
import {
  GET_CASE_QUESTIONNAIRE_SECTION,
  GetCaseQuestionnaireSectionResponse
} from 'share/queries/cases'
import {
  COMPLETE_QUESTIONNAIRE_SECTION,
  CompleteQuestionnaireSectionVariables,
  CompleteQuestionnaireSectionResponse
} from 'share/mutations/cases'

import {
  setActiveSection,
  loadSectionRecordFromResponse,
  ActiveQuestionnaireSection,
  setCurrentActiveQuestionId,
  setCurrentActiveGroup,
  setQuestionsWithDataInTransformModel,
  setGroupsWithDataInTransformModel
} from 'reducers/activesection/activesectionSlice'
import GoBackHeader from 'components/organisms/GoBackHeader'
import { loadingSpinnerPortal } from 'components/atoms/LoadingSpinner'
import Button from 'react-bootstrap/Button'
import QuestionnaireSectionSummaryCard from '../Components/QuestionnaireSectionSummaryCard'
import {
  QuestionnaireSectionSummaryCardWrapper,
  QuestionnaireSectionSummaryContainer
} from './styles'
import ImageFallback from 'components/molecules/ImageFallback'
import QuestionnaireSectionGroupSummaryCard from '../Components/QuestionnaireSectionGroupSummaryCard'
import { Group, Question } from '../constants'
import { cloneDeep } from 'lodash'
import { isObjectEmpty } from '../utils'
import { MatterClientPortalQuestionnaireSectionStatus } from 'share/domain/case'
import { QuestionnaireSectionSummaryReadOnlyNotification } from '../QuestionnaireSectionGroupSummary/styles'

interface ParamTypes {
  sectionId: string
}

const QuestionnaireSectionSummary: FC = () => {
  const dispatch = useAppDispatch()
  const sessionStor = window.sessionStorage
  const activeCase = useAppSelector((state) => state.activeCase)
  const activeSection = useAppSelector((state) => state.activeSection)
  const activeSectionStatus = activeSection.section?.sectionStatus

  const authentication = useAppSelector((state) => state.authentication)
  const [summaryHeader, setSummaryHeader] = useState<string>('Summary')

  const activeQuestionId = useAppSelector(
    (state) => state.activeSection.section?.currentActiveQuestionId
  )
  const activeGroup = useAppSelector(
    (state) => state.activeSection.section?.currentActiveGroup
  )

  const currentSectionUITransformModel = useAppSelector(
    (state) => state.activeSection.section?.sectionUiTransformedModel
  )
  const hasSectionUITransformModel = useRef<boolean>(
    Boolean(currentSectionUITransformModel)
  )

  const [toQuestionnaireOverview, setToQuestionnaireOverview] = useState(false)
  const [mounted, setMounted] = useState(true)

  const [loadingDelay, setLoadingDelay] = useState(false)

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [areQuestionsComplete, setAreQuestionsComplete] =
    useState<boolean>(false)
  const history = useHistory()
  const { sectionId } = useParams<ParamTypes>()

  const [sectionUiTransformedModel, setsectionUiTransformedModel] =
    // eslint-disable-next-line
    useState<any>({})

  const [getQuestionnaireSection, { data, error, called, refetch }] =
    useLazyQuery<GetCaseQuestionnaireSectionResponse>(
      GET_CASE_QUESTIONNAIRE_SECTION,
      {
        fetchPolicy: 'network-only'
      }
    )

  const [completeQuestionnaireSection] = useMutation<
    CompleteQuestionnaireSectionResponse,
    CompleteQuestionnaireSectionVariables
  >(COMPLETE_QUESTIONNAIRE_SECTION)

  function getCaseId() {
    return activeCase.matter && activeCase.matter.id ? activeCase?.matter.id : 0
  }

  function getSectionId() {
    if (activeSection) {
      if (activeSection.section) {
        if (activeSection.section.id) {
          return activeSection.section.id
        }
      }
    }
    return 0
  }

  function getFormattedJsonObject(jsonString: string) {
    if (jsonString && jsonString.length) {
      return JSON.parse(jsonString, (key, value) => {
        if (value === null) {
          return undefined
        }
        if (Array.isArray(value) && value.length === 0) {
          return undefined
        }
        if (
          typeof value === 'object' &&
          Object.values(value).every((v) => v === null || v === undefined)
        ) {
          return undefined
        }
        return value
      })
    }
    return {}
  }

  function getSectionQuestionData(
    activeSection: ActiveQuestionnaireSection,
    questionKey: string
    // eslint-disable-next-line
  ): any {
    if (data && data.cases && data.cases.caseQuestionnaireSectionById) {
      for (const element of data.cases.caseQuestionnaireSectionById
        .sectionDataRecords) {
        if (element.questionKey === questionKey) {
          return {
            data: getFormattedJsonObject(element.questionDataJsonText),
            questionCompleteFlag: element.questionCompleteFlag
          }
        }
      }
    }
    return {}
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function setDataToQuestions(questions: any) {
    const clonedQuestions = cloneDeep(questions)
    for (const element of clonedQuestions) {
      const questionKey = element.Value
      if (!element.UnitForm.Data) {
        const sectionQuestionData = getSectionQuestionData(
          activeSection,
          questionKey
        )
        element.UnitForm.Data = sectionQuestionData?.data || {}
        element.UnitForm.QuestionCompleteFlag =
          sectionQuestionData.questionCompleteFlag || false
      }
    }
    return clonedQuestions
  }

  // load data section for each unitform
  function loadSavedDataIntoUiTransformedModel(): void {
    try {
      const questions = sectionUiTransformedModel.Questions
      const groups = sectionUiTransformedModel.Groups

      if (questions && questions.length) {
        dispatch(
          setQuestionsWithDataInTransformModel(setDataToQuestions(questions))
        )
      } else {
        if (groups && groups.length) {
          const clonedGroups = cloneDeep(groups)
          for (const element of clonedGroups) {
            const questions = element.Questions
            element.Questions = setDataToQuestions(questions)
          }
          dispatch(setGroupsWithDataInTransformModel(clonedGroups))
        }
      }
    } catch (e) {
      console.log('error loading saved data into ui transform', e)
    }
  }

  async function completeQuestionnaireSectionHelper(
    caseId: number,
    sectionId: number
  ): Promise<boolean> {
    let result = false
    try {
      const input = {
        caseId: caseId,
        sectionId: sectionId
      }
      setLoadingDelay(true)
      const completeResult = await completeQuestionnaireSection({
        variables: { request: input }
      })
      if (completeResult.data?.cases.completeQuestionnaireSection.success) {
        setLoadingDelay(false)
        result = true
      }
    } catch (e) {
      setLoadingDelay(false)

      result = false
    }

    return result
  }

  async function completeSectionClick(caseId: number, sectionId: number) {
    const completeSectionResult = await completeQuestionnaireSectionHelper(
      caseId,
      sectionId
    )

    if (completeSectionResult) {
      console.log('section is complete')
      setToQuestionnaireOverview(true)
    } else {
      console.log('error completing section')
    }
  }

  function goBackHeaderOnClick() {
    history.push('/dashboard/questionnaires')
  }

  const getSectionUiTransformedModel = (caseId: number, sectionId: number) => {
    const BackendURL = graphqlEndpoint.replace('/graphql', '')
    const requestHeaders: HeadersInit = new Headers()
    requestHeaders.set('Authorization', `Bearer ${authentication.idToken}`)
    requestHeaders.set('Access_Token', `${authentication.accessToken}`)
    const endpoint = 'uiTransformedModel'
    const options = {
      method: 'GET',
      headers: requestHeaders
    }

    fetch(`${BackendURL}api/cases/${caseId}/${sectionId}/${endpoint}`, options)
      .then((data) => data.json())
      .then((json) => {
        if (mounted) {
          setsectionUiTransformedModel(json)
        }
      })
      .catch((err) => {
        setLoadingDelay(false)
        console.log(err)
      })
  }

  useEffect(() => {
    return () => {
      setMounted(false)
    }
  }, [])

  useEffect(() => {
    if (
      activeSection.section?.sectionName !== 'Personal Information' &&
      activeSection.section?.sectionName
    ) {
      if (mounted) {
        setSummaryHeader(activeSection.section?.sectionName + ' Summary')
      }
    }
  }, [activeSection])

  useEffect(() => {
    const abortController = new AbortController()
    setLoadingDelay(true)
    if (!hasSectionUITransformModel.current) {
      let localSectionId = 0
      if (
        sectionId &&
        activeCase &&
        activeCase.matter &&
        activeCase.matter.id
      ) {
        localSectionId = parseInt(sectionId)
        getSectionUiTransformedModel(activeCase.matter.id, localSectionId)
      }
    } else {
      setsectionUiTransformedModel(currentSectionUITransformModel)
    }

    return () => {
      abortController.abort()
    }
  }, [sectionId])

  useEffect(() => {
    let localSectionId = 0
    if (
      sectionId &&
      activeCase &&
      activeCase.matter &&
      activeCase.matter.id &&
      !isObjectEmpty(sectionUiTransformedModel)
    ) {
      localSectionId = parseInt(sectionId)
      getQuestionnaireSection({
        variables: {
          caseId: activeCase.matter.id,
          sectionId: localSectionId
        }
      })
    }

    // Cleanup function to cancel the query when the component unmounts
    return () => {
      if (!called || !refetch) {
        // If the query hasn't been called yet or if the refetch function doesn't exist, there's nothing to cancel
        return
      }
      // Cancel the query using the refetch function with the {cancel: true} option
      refetch({ cancel: true })
    }
  }, [sectionUiTransformedModel])

  function isGroupArrayCompleted(groupArray: Group[]): boolean {
    // Iterate over the groups in the array
    return groupArray.every((group) => {
      // Check if every question in the group is complete
      return group.Questions.every((question) => {
        return question.UnitForm.QuestionCompleteFlag
      })
    })
  }

  useEffect(() => {
    if (data) {
      const activeSection = loadSectionRecordFromResponse(
        data,
        sectionUiTransformedModel
      )
      // load array of sectionDataRecords to sectionUiTransformedModel unitform part here
      dispatch(setActiveSection(activeSection))
      loadSavedDataIntoUiTransformedModel()

      setLoadingDelay(false)
    }
  }, [data])

  useEffect(() => {
    const questions = currentSectionUITransformModel?.Questions
    const groups = currentSectionUITransformModel?.Groups
    if (questions && questions.length) {
      // eslint-disable-next-line prettier/prettier
      const areQuestionsComplete = questions.every((question: Question) => {
        return question.UnitForm.QuestionCompleteFlag
      })
      if (mounted) {
        setAreQuestionsComplete(areQuestionsComplete)
      }
    }

    if (groups && groups.length && mounted) {
      setAreQuestionsComplete(isGroupArrayCompleted(groups))
    }
  }, [currentSectionUITransformModel])

  if (error) {
    return <div>Error ...</div>
  }

  if (!activeSection) {
    return <div></div>
  }

  if (toQuestionnaireOverview)
    return <Redirect to={`/dashboard/questionnaires`} />

  const handleClick = (questionId: number) => {
    dispatch(setCurrentActiveQuestionId(questionId))
  }

  const handleGroupClick = (groupId: number) => {
    dispatch(setCurrentActiveGroup(groupId))
  }

  if (activeGroup != null) {
    return (
      <Redirect
        to={{
          pathname: `/dashboard/questionnaires/groupsummary`,
          state: { navigationQuestionId: activeQuestionId }
        }}
      />
    )
  }

  if (activeQuestionId != null) {
    return (
      <Redirect
        to={{
          pathname: `/dashboard/questionnaires/question`,
          state: { navigationQuestionId: activeQuestionId }
        }}
      />
    )
  }

  return (
    <>
      {loadingDelay && loadingSpinnerPortal()}

      <GoBackHeader
        text={activeSection.section?.sectionName}
        onClick={goBackHeaderOnClick}
      />
      <QuestionnaireSectionSummaryContainer>
        {activeSectionStatus ===
          MatterClientPortalQuestionnaireSectionStatus.Submitted ||
        activeSectionStatus ===
          MatterClientPortalQuestionnaireSectionStatus.Imported ? (
          <QuestionnaireSectionSummaryReadOnlyNotification>
            <div style={{ display: 'flex' }}>
              <span className='mdi mdi-information blue-info-icon mr-1'></span>
              <span data-testid='questionnaire-summary-read-only-notification'>
                This section was submitted. You may view it in read-only mode.
              </span>
            </div>
          </QuestionnaireSectionSummaryReadOnlyNotification>
        ) : (
          <></>
        )}

        {activeSection.section?.isRejected && (
          <div className='attorney-comment-card'>
            <div className='attorney-comment-header'>Attorney Comments</div>
            <div className='attorney-comment-container'>
              <div className='attorney-image'>
                <ImageFallback
                  src={`${BACKEND_URL}${
                    sessionStor.getItem('acase-matter-attorneyavatar') as string
                  }`}
                  alt='Attorney avatar'
                  fallback={<span className='mdi mdi-account-circle' />}
                />
              </div>
              <div>
                <div className='attorney-name'>
                  {activeCase.matter?.attorneyName}
                </div>
                <div className='attorney-comment'>
                  {activeSection.section?.comments?.substring(9)}
                </div>
              </div>
            </div>
          </div>
        )}

        {summaryHeader && <h2 className='summary-header'> {summaryHeader} </h2>}
        <div className='summary-description'>
          {activeSection?.section?.sectionDescription.split('\\n').join('\n')}
        </div>
        {currentSectionUITransformModel ? (
          <QuestionnaireSectionSummaryCardWrapper>
            {currentSectionUITransformModel.Questions?.length ? (
              currentSectionUITransformModel.Questions?.map(
                // eslint-disable-next-line
                (value: any, index: number) => {
                  return (
                    <QuestionnaireSectionSummaryCard
                      key={value?.Name}
                      {...value}
                      handleClick={handleClick}
                    ></QuestionnaireSectionSummaryCard>
                  )
                }
              )
            ) : (
              <></>
            )}
            {currentSectionUITransformModel.Groups?.length ? (
              currentSectionUITransformModel.Groups?.map(
                // eslint-disable-next-line
                (value: any, index: number) => {
                  return (
                    <QuestionnaireSectionGroupSummaryCard
                      key={value?.Name}
                      {...value}
                      handleClick={handleGroupClick}
                    ></QuestionnaireSectionGroupSummaryCard>
                  )
                }
              )
            ) : (
              <></>
            )}
          </QuestionnaireSectionSummaryCardWrapper>
        ) : (
          <></>
        )}
        {activeSectionStatus !==
          MatterClientPortalQuestionnaireSectionStatus.Submitted &&
        activeSectionStatus !==
          MatterClientPortalQuestionnaireSectionStatus.Imported ? (
          <>
            <div className='btn-container'>
              <Button
                onClick={() => {
                  completeSectionClick(getCaseId(), getSectionId())
                }}
                style={{ marginLeft: 'auto', marginRight: 'auto' }}
                disabled={!areQuestionsComplete}
              >
                Mark as Complete
              </Button>
            </div>
            <div className='save-finish-later-link'>
              <a href='#' onClick={goBackHeaderOnClick}>
                Save and Finish Later
              </a>
            </div>
          </>
        ) : (
          <></>
        )}
        <div className='questionnaire-summary-important'>
          <span className='mdi mdi-information-outline' />
          <span> Important</span>
        </div>
        <div className='questionnaire-summary-important-text'>
          You can still add, edit, and delete any records in this section before
          sending the questionnaire to your attorney.
        </div>
      </QuestionnaireSectionSummaryContainer>
    </>
  )
}

export default QuestionnaireSectionSummary
